import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { bs } from '../../../../styles/styles';
import { Button } from '../../../Button';
import { CustomDateRange } from '../../../CustomDateRange';
import { Dropdown } from '../../../Dropdown';
import { DisclosureIcon } from '../../../svgs/icons/DisclosureIcon';
import { styleSheet as dropdownStyles } from '../../DropdownComponent/styles';
import { DateRangeOption, IDayOption } from '../../models';
import { dayOptions } from '../../utils';
import { styleSheet } from './styles';

/**
 * Past-facing policy date-range filter. Presets are lower bounds (e.g. "1 month" = on or after a
 * month ago), suited to historical dates such as Origination (Sold/Close) and Effective. For
 * upcoming-date fields (e.g. Renewal), use FutureRenewalDateFilterDropdown instead.
 */
export const PolicyDateRangeFilterDropdown = ({
	fieldName,
	selectedMinimumDate,
	setSelectedMinimumDate,
	selectedMaximumDate,
	setSelectedMaximumDate,
	anchorClassName,
}: {
	fieldName: string;
	selectedMinimumDate: string;
	setSelectedMinimumDate: React.Dispatch<React.SetStateAction<string>>;
	selectedMaximumDate: string;
	setSelectedMaximumDate: React.Dispatch<React.SetStateAction<string | null>>;
	anchorClassName?: string;
}) => {
	const [isOpen, setIsOpen] = React.useState(false);
	const [selectedDayOption, setSelectedDayOption] = React.useState<IDayOption | null>(null);

	const isCustomSelected = React.useMemo(() => {
		return selectedDayOption?.value === DateRangeOption.Custom;
	}, [selectedDayOption?.value]);

	React.useEffect(() => {
		if (selectedMinimumDate && selectedMaximumDate) {
			const customDayOption = dayOptions.find(option => option.value === DateRangeOption.Custom);
			setSelectedDayOption(customDayOption ?? null);
			return;
		}
		const match = dayOptions.find((option: IDayOption) => option.value === selectedMinimumDate);
		setSelectedDayOption(match ?? null);
	}, [selectedMaximumDate, selectedMinimumDate]);

	const getDateRangeMessage = () => {
		if (!selectedMinimumDate || !selectedDayOption) {
			return fieldName;
		}

		if (selectedDayOption.value === DateRangeOption.Custom && selectedMinimumDate && selectedMaximumDate) {
			const selectedMinimumDateString = moment(selectedMinimumDate).format('MM/DD');
			const selectedMaximumDateString = moment(selectedMaximumDate).format('MM/DD');
			return `${fieldName} ${selectedMinimumDateString} - ${selectedMaximumDateString}`;
		}
		const key = selectedDayOption.key;
		return `${fieldName}/ ${DateRangeOption[key]}`;
	};

	const dropdownMessage = getDateRangeMessage();

	const filteredDayOptions = dayOptions.filter(option => {
		if (option.value === DateRangeOption.Custom && selectedDayOption?.value === DateRangeOption.Custom) {
			return false;
		}
		return true;
	});

	const selectDateRangeOption = (newDayOption: IDayOption, ev: React.MouseEvent<HTMLButtonElement>) => {
		if (newDayOption.value === DateRangeOption.Custom) {
			setSelectedDayOption(newDayOption);
			ev.stopPropagation();
			ev.preventDefault();
		} else {
			// Presets are past-facing lower bounds (e.g. "1 month" = on or after a month ago).
			setSelectedMinimumDate(newDayOption.value);
			setSelectedMaximumDate(null);
			setIsOpen(false);
		}
	};

	const resetFilter = () => {
		setSelectedMinimumDate(null);
		setSelectedMaximumDate(null);
		setSelectedDayOption(null);
	};

	return (
		<Dropdown
			anchor={
				<div className={css(dropdownStyles.dropdownAnchor)}>
					<div className={css(bs.truncateText)}>{dropdownMessage}</div>
					<DisclosureIcon className={css(dropdownStyles.dropdownIcon)} />
				</div>
			}
			anchorClassName={anchorClassName}
			disabled={false}
			className={css(dropdownStyles.dropdownInline, isOpen ? dropdownStyles.dropdownInlineOpen : null)}
			contentClassName={css(dropdownStyles.dropdownMenu, styleSheet.dropdownMenuOverrides)}
			contentPositionY='bottom'
			contentInnerClassName={css(styleSheet.dropdownContentInnerOverrides)}
			isOpen={isOpen}
			onOpenChanged={() => setIsOpen(!isOpen)}
			openOnClick={true}
		>
			<div className={css(dropdownStyles.dropdown)}>
				<div>
					{isCustomSelected ? (
						<CustomDateRange
							endDateRequired
							resetDates={() => {
								setSelectedMinimumDate(null);
								setSelectedMaximumDate(null);
							}}
							defaultOpen={!selectedMaximumDate}
							from={selectedMinimumDate ? moment(selectedMinimumDate).toDate() : new Date()}
							to={selectedMaximumDate ? moment(selectedMaximumDate).toDate() : null}
							onChange={(from, to) => {
								setSelectedMinimumDate(moment(from).format('YYYY-MM-DD'));
								setSelectedMaximumDate(to ? moment(to).format('YYYY-MM-DD') : moment(from).format('YYYY-MM-DD'));
							}}
							disableScrollTo
						/>
					) : null}
				</div>

				<div>
					{!isCustomSelected &&
						filteredDayOptions.map(dayOption => {
							return (
								<Button
									label={dayOption.label}
									key={`policy-date-range-option-${dayOption.key}`}
									onClick={(e: React.MouseEvent<HTMLButtonElement>) => selectDateRangeOption(dayOption, e)}
									kind='custom'
									className={css(
										dropdownStyles.option,
										selectedDayOption?.key === dayOption.key ? dropdownStyles.selected : null
									)}
								/>
							);
						})}
				</div>

				<Button label='Reset Filter' kind='custom' className={css(bs.brandLink)} type='button' onClick={resetFilter} />
			</div>
		</Dropdown>
	);
};
